import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { deriveReferralFlag } from "@/lib/referralRedemption";

/**
 * Remote kill-switch for referrals — the invite link, the greeting page and the
 * manual code entry all hang off this one row.
 *
 * This MUST ship in the first build that carries any of them. Native has no
 * over-the-air path: a link shipped before the hub is live would sit on devices
 * failing for weeks until the next store review. Flipping
 * `app_config.referrals_enabled` on is what makes it appear, everywhere at once
 * and with no new binary.
 *
 * HUB STATE, measured 2026-09-22 — the schema landed, the programme did not.
 * All five `referral_*` tables now exist in production Neon (an earlier note here
 * said they did not; that was true on 2026-09-09 and rotted). Every one of them is
 * EMPTY: zero rows in `referral_programs`, so there is no programme for a code to
 * belong to, and a redemption still has nothing to succeed against. Re-check in
 * ten seconds before trusting either half of that sentence:
 *
 *   select (select count(*) from referral_programs) as programs,
 *          (select count(*) from referral_codes)    as codes;
 *
 * Not verified here and still open: whether the hub's `/api/referrals/*` routes
 * are present in the DEPLOYED build. Schema is necessary, not sufficient.
 *
 * ONE key, not one per platform, unlike `useIapEnabled`. That hook is split
 * because Apple and Google went live weeks apart; a referral program is a single
 * business decision that starts everywhere on the same day.
 *
 * RUNS ON WEB TOO, unlike every other kill-switch here — see `deriveReferralFlag`
 * for why. An invite link opens in a browser whenever the friend has no app yet,
 * which is the common case, so `deriveFlagState`'s native-only rule would switch
 * the page off for precisely the people it exists to greet.
 *
 * Fails CLOSED on a missing row or a read error. No row needs to exist to ship
 * this; adding one is how it turns on.
 */
export function useReferralsEnabled(): { enabled: boolean; loading: boolean } {
  const { data, isError } = useQuery<boolean>({
    queryKey: ["referrals-enabled"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select("enabled")
        .eq("key", "referrals_enabled")
        .maybeSingle();
      if (error) return false;
      return data?.enabled === true;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

  return deriveReferralFlag({ data, isError });
}
